import { useCallback, useRef } from 'react'

/**
 * 紙を1枚ずつ印刷し、全部の紙を印刷できたら onComplete を1回呼んで true を返す。
 * 失敗した紙で止まり、次の print() はその紙から続ける（印刷できた紙は出し直さない）
 * printSheet には、その処理が最後に呼ばれた print() かを返す isLatest を渡す（古い処理がプリンターへの送信の前に止まれるように）
 */
export const useResumablePrint = ({
  sheetCount,
  printSheet,
  onError,
  onComplete,
}: {
  sheetCount: number
  printSheet: (sheetIndex: number, isLatest: () => boolean) => Promise<void>
  onError: (error: unknown, sheetIndex: number) => void
  onComplete: () => void
}): { print: () => Promise<boolean> } => {
  // 画面に出す値ではなく、非同期の印刷処理から常に最新を読みたいため、state ではなく ref で持つ
  const nextSheetIndex = useRef(0)
  // 最初の印刷の応答待ちの間も再印刷のボタンを押せる。2つの処理が別々に紙を進めたり onComplete を2回呼んだりしないよう、最後に呼ばれた print() だけを有効にする
  const latestPrintId = useRef(0)

  const print = useCallback(async (): Promise<boolean> => {
    const printId = ++latestPrintId.current
    const isLatest = () => printId === latestPrintId.current

    for (let sheetIndex = nextSheetIndex.current; sheetIndex < sheetCount; sheetIndex++) {
      try {
        await printSheet(sheetIndex, isLatest)
      } catch (error) {
        if (isLatest()) onError(error, sheetIndex)
        return false
      }
      if (!isLatest()) return false
      nextSheetIndex.current = sheetIndex + 1
    }
    onComplete()
    return true
  }, [sheetCount, printSheet, onError, onComplete])

  return { print }
}
